import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLMotionProps } from 'framer-motion'
import { motion } from 'framer-motion'
import { X, CircleCheck } from 'lucide-react'
import * as React from 'react'

import { BorderBeam } from 'src/shared/components/ui/animation/border-beam'
import PulsatingDots from 'src/shared/components/ui/animation/pulsating-dots-loader'
import { cn } from 'src/shared/utils/cn'

const buttonVariants = cva(
  'inline-flex transform-gpu items-center justify-center whitespace-normal font-semibold transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-30',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        fadedDestructive: 'border border-destructive/40 bg-destructive/20 text-foreground',
        outline: 'border border-primary bg-transparent hover:text-accent-foreground',
        movingOutline: 'relative bg-transparent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        fadedSecondary: 'border border-secondary/60 bg-secondary/5 text-foreground',
        secondaryInverse: 'bg-secondaryInverse text-secondaryInverse-foreground',
        accent: 'border bg-accent text-accent-foreground',
        ghost: 'hover:bg-transparent hover:text-accent-foreground',
        like: 'hover:text-accent-foreground',
        link: 'text-primary underline underline-offset-4',
        multiSelect:
          'relative flex w-full items-center rounded-xl border bg-background p-4 ring-offset-background data-[state=checked]:border-primary data-[state=checked]:bg-primary/5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40',
      },
      dark: {
        true: '',
        false: '',
      },
      size: {
        default: 'min-h-12 rounded-md px-4 py-2',
        sm: 'min-h-9 rounded-md px-3 text-sm',
        lg: 'min-h-11 rounded-md px-8',
        xl: 'min-h-14 rounded-md px-10 text-lg',
        icon: 'size-10 shrink-0 rounded-full',
      },
      isLoading: {
        true: 'cursor-not-allowed [&>*]:opacity-100',
        false: '',
      },
    },
    compoundVariants: [
      {
        isLoading: true,
        class: 'opacity-70',
      },
      {
        variant: 'default',
        dark: true,
        class: 'bg-background text-foreground hover:bg-background/90',
      },
      {
        variant: 'outline',
        dark: true,
        class:
          'border-background bg-transparent text-background hover:border-background/80 hover:bg-transparent hover:text-primary-foreground/80',
      },
      {
        variant: 'secondary',
        dark: true,
        class: 'bg-background/20 text-background hover:bg-background/30',
      },
      {
        variant: 'ghost',
        dark: true,
        class: 'text-background hover:bg-background/10',
      },
      {
        variant: 'link',
        dark: true,
        class: 'text-background hover:text-background/80',
      },
      {
        variant: 'multiSelect',
        dark: true,
        class:
          'border-background/20 bg-background/10 text-background data-[state=checked]:border-background data-[state=checked]:bg-background/20 hover:bg-background/20',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      isLoading: false,
      dark: false,
    },
  },
)

export interface ButtonProps
  extends Omit<HTMLMotionProps<'button'>, keyof VariantProps<typeof buttonVariants> | 'children'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  showCloseIcon?: boolean
  isSelected?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  href?: string
  children?: React.ReactNode
  borderBeamProps?: React.ComponentProps<typeof BorderBeam>
  pulsatingDotsClassName?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      dark,
      asChild = false,
      isLoading = false,
      showCloseIcon = false,
      isSelected,
      children,
      disabled,
      leftIcon,
      rightIcon,
      href,
      borderBeamProps,
      pulsatingDotsClassName,
      ...props
    },
    ref,
  ) => {
    const handleClick = () => {
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer')
      }
    }

    const renderButtonContent = () => (
      <>
        {isLoading ? (
          <PulsatingDots
            className={cn('size-2', dark ? 'text-background' : 'text-primary-foreground', pulsatingDotsClassName)}
          />
        ) : (
          <>
            {leftIcon && <div className="mr-2">{leftIcon}</div>}
            {children}
            {rightIcon && <div className="ml-auto">{rightIcon}</div>}
          </>
        )}
        {variant === 'multiSelect' && (
          <div className="ml-auto pl-2">
            {isSelected ? (
              <CircleCheck className={cn('size-5', dark ? 'text-background' : 'text-primary')} />
            ) : (
              <CircleCheck className={cn('size-5', dark ? 'text-background/35' : 'text-primary/35')} />
            )}
          </div>
        )}
        {showCloseIcon && (
          <div
            className={cn(
              'absolute right-3 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded-full',
              dark ? 'bg-background' : 'bg-primary',
            )}
          >
            <X className={cn('size-3', dark ? 'text-primary' : 'text-primary-foreground')} />
          </div>
        )}
        {variant === 'movingOutline' && <BorderBeam {...borderBeamProps} />}
      </>
    )

    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, dark, isLoading, className }), 'relative')}
          ref={ref}
          onClick={handleClick}
        >
          {renderButtonContent()}
        </Slot>
      )
    }

    return (
      <motion.button
        className={cn(buttonVariants({ variant, size, dark, isLoading, className }), 'relative')}
        ref={ref}
        disabled={disabled || isLoading}
        data-state={isSelected ? 'checked' : 'unchecked'}
        onClick={handleClick}
        style={{ transformOrigin: 'center' }}
        {...props}
      >
        {renderButtonContent()}
      </motion.button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
